/* 온보딩 퀘스트(기획 §14.12~14.14 · 2차) — 줄기(캐릭터 · 세계관 · 명령어 · 상태창)마다 한 칸짜리 스텝.
   홈 카드에는 지금 스텝 하나만, 누르면 게임 퀘스트창(모달 · 모바일 바텀시트)에 줄기 전체와 업적이 보인다.
   수락은 없다 — 끝내면 받기. 스텝은 한 대상(퀘스트가 따라가는 캐릭터 · 세계관 · 명령어 · 상태창)에 쌓인다.
   보상 값은 서버 설정(어드민)이 정할 임시 값이고, 지급도 서버 몫이다 — 지금은 받았다는 표시만 남긴다. */
import { usersOf } from '@/features/studio-renew/store'
import { startsDone } from '@/features/studio-renew/steps'

import type {
  Character,
  Chat,
  Command,
  QuestState,
  StatusBoard,
  World,
} from '@/features/studio-renew/types'

export type QuestTrack = 'character' | 'world' | 'command' | 'status'
const QUEST_TRACKS: QuestTrack[] = ['character', 'world', 'command', 'status']

/** 대상 하나로 판정하는 데 필요한 것 */
type QuestCtx = {
  character?: Character
  world?: World
  command?: Command
  status?: StatusBoard
  chats: Chat[]
  characters: Character[]
  quest: QuestState
}

/**
 * kind · step — 누르면 갈 편집 칸(대상 id 는 카드가 붙인다). work 는 대상이 든 작품으로,
 * talk 는 이동 없이 카드 안에서 미리 대화를 연다.
 */
type QuestStep = {
  id: string
  title: string
  hint: string
  reward: number
  kind: 'character' | 'world' | 'command' | 'status' | 'work' | 'talk'
  step: string
  done: (x: QuestCtx) => boolean
}

const filled = (v?: string) => !!v && v.trim().length > 0
const worksOf = (x: QuestCtx) =>
  x.chats.filter(
    (c) => !!x.character && c.characterIds.includes(x.character.id)
  )
const linked = (x: QuestCtx, kind: 'status' | 'command') => {
  const id = kind === 'status' ? x.status?.id : x.command?.id
  return (
    !!id &&
    usersOf(Object.fromEntries(x.chats.map((c) => [c.id, c])), kind, id)
      .length > 0
  )
}

export const TRACK_LABEL: Record<QuestTrack, string> = {
  character: '캐릭터 퀘스트',
  world: '세계관 퀘스트',
  command: '명령어 퀘스트',
  status: '상태창 퀘스트',
}

export const QUEST_STEPS: Record<QuestTrack, QuestStep[]> = {
  character: [
    {
      id: 'c-name',
      title: '이름 짓기',
      hint: '이용자가 부를 이름을 정해요',
      reward: 30,
      kind: 'character',
      step: 'profile',
      done: (x) => filled(x.character?.name),
    },
    {
      id: 'c-persona',
      title: '캐릭터 설정 쓰기',
      hint: '성격 · 말투 · 나와의 관계를 자유롭게',
      reward: 30,
      kind: 'character',
      step: 'persona',
      done: (x) => filled(x.character?.persona),
    },
    {
      id: 'c-talk',
      title: '내 캐릭터에게 말 걸기',
      hint: '방금 만든 캐릭터에게 한마디 건네 봐요',
      reward: 20,
      kind: 'talk',
      step: '',
      done: (x) => x.quest.talked,
    },
    {
      id: 'c-image',
      title: '대표 이미지 올리기',
      hint: '목록에서 가장 먼저 보이는 얼굴',
      reward: 30,
      kind: 'character',
      step: 'profile',
      done: (x) => filled(x.character?.image),
    },
    {
      id: 'c-tagline',
      title: '한 줄 소개 쓰기',
      hint: '이름 아래 붙는 짧은 소개',
      reward: 20,
      kind: 'character',
      step: 'profile',
      done: (x) => filled(x.character?.tagline),
    },
    // 인트로는 작품의 시작에 쓴다 — 이 캐릭터가 든 작품의 인트로 단계로
    {
      id: 'c-intro',
      title: '작품 인트로 쓰기',
      hint: '대화를 열면 이용자가 처음 보는 장면',
      reward: 30,
      kind: 'work',
      step: 'starts',
      done: (x) => worksOf(x).some(startsDone),
    },
    {
      id: 'c-examples',
      title: '말버릇 한 줄',
      hint: '말투를 굳히는 가장 쉬운 방법',
      reward: 20,
      kind: 'character',
      step: 'habits',
      done: (x) => !!x.character?.habits.some((h) => filled(h.text)),
    },
  ],
  world: [
    {
      id: 'w-name',
      title: '세계관 이름 짓기',
      hint: '캐릭터들이 사는 곳의 이름',
      reward: 30,
      kind: 'world',
      step: 'basic',
      done: (x) => (x.world?.name.trim().length ?? 0) >= 2,
    },
    {
      id: 'w-summary',
      title: '한 줄 소개 쓰기',
      hint: '세계관 탭 카드에 보이는 한 줄',
      reward: 20,
      kind: 'world',
      step: 'basic',
      done: (x) => filled(x.world?.summary),
    },
    {
      id: 'w-body',
      title: '세계관 설정 쓰기',
      hint: '배경 · 규칙 · 세력을 적어요',
      reward: 30,
      kind: 'world',
      step: 'body',
      done: (x) => filled(x.world?.body),
    },
    {
      id: 'w-place',
      title: '장소 하나 추가하기',
      hint: '첫 장면과 앨범에서 고를 수 있어요',
      reward: 20,
      kind: 'world',
      step: 'body',
      done: (x) => !!x.world?.places.some((p) => filled(p.name)),
    },
    {
      id: 'w-keyword',
      title: '키워드북 채우기',
      hint: '키워드가 나오면 불러올 설정',
      reward: 30,
      kind: 'world',
      step: 'keywords',
      done: (x) =>
        !!x.world?.keywords.some((k) => k.keys.length > 0 && filled(k.text)),
    },
    {
      id: 'w-cast',
      title: '캐릭터 한 명 들이기',
      hint: '캐릭터의 세계관 단계에서 골라요',
      reward: 30,
      kind: 'character',
      step: 'world',
      done: (x) =>
        !!x.world && x.characters.some((c) => c.worldId === x.world?.id),
    },
  ],
  command: [
    {
      id: 'm-key',
      title: '명령어 키 정하기',
      hint: '!일기처럼 내 명령어의 키와 화면 이름',
      reward: 20,
      kind: 'command',
      step: 'edit',
      done: (x) => (x.command?.key.length ?? 0) > 1 && filled(x.command?.title),
    },
    {
      id: 'm-desc',
      title: '설명 쓰기',
      hint: '캐릭터가 내용을 만들 때 참고해요',
      reward: 20,
      kind: 'command',
      step: 'edit',
      done: (x) => filled(x.command?.description),
    },
    {
      id: 'm-html',
      title: '결과 화면 꾸미기',
      hint: 'HTML 로 나만의 화면을 짜요',
      reward: 30,
      kind: 'command',
      step: 'edit',
      done: (x) => filled(x.command?.html),
    },
    {
      id: 'm-link',
      title: '작품에 연결하기',
      hint: '작품 꾸미기의 명령어 단계에서 골라요',
      reward: 30,
      kind: 'work',
      step: 'commands',
      done: (x) => linked(x, 'command'),
    },
  ],
  status: [
    {
      id: 's-name',
      title: '상태창 이름 짓기',
      hint: '내 목록에서 알아볼 이름',
      reward: 20,
      kind: 'status',
      step: 'edit',
      done: (x) => filled(x.status?.name),
    },
    {
      id: 's-field',
      title: '항목 하나 만들기',
      hint: '호감도 · 장소처럼 대화 중 바뀌는 값',
      reward: 20,
      kind: 'status',
      step: 'edit',
      done: (x) => !!x.status?.fields.some((f) => filled(f.key)),
    },
    {
      id: 's-three',
      title: '항목 세 개 채우기',
      hint: '묶음 불러오기로 빠르게 채워도 돼요',
      reward: 30,
      kind: 'status',
      step: 'edit',
      done: (x) =>
        (x.status?.fields.filter((f) => filled(f.key)).length ?? 0) >= 3,
    },
    {
      id: 's-html',
      title: '디자인 고르기',
      hint: '템플릿 중 하나를 골라 색만 바꿔도 돼요',
      reward: 30,
      kind: 'status',
      step: 'edit',
      done: (x) => filled(x.status?.html),
    },
    {
      id: 's-link',
      title: '작품에 연결하기',
      hint: '작품 꾸미기의 상태창 단계에서 골라요',
      reward: 30,
      kind: 'work',
      step: 'status',
      done: (x) => linked(x, 'status'),
    },
  ],
}

const ALL_STEPS = QUEST_TRACKS.flatMap((t) => QUEST_STEPS[t])
export const questTitle = (id: string | null) =>
  ALL_STEPS.find((s) => s.id === id)?.title
